import { getAppEndpointVersion } from 'helpers'
import axios from './axiosInstance'
import constants from '../constants'

const resolveApiHost = app => {
  return app?.appRegion?.meta?.apiHost || 'api.cometchat.com'
}

const getBaseUrl = app => {
  const apiHost = resolveApiHost(app)
  const protocol = app.appRegion.meta.useSSL ? 'https://' : 'http://'
  return `${protocol}${apiHost}/${getAppEndpointVersion(app)}/ai-agents/agent-builder`
}

const getHeaders = app => ({
  appId: app.id,
  apiKey: app.accessKey,
  'Content-Type': 'application/json',
  Accept: 'application/json',
  chatapiversion: 'v3.0',
})

/**
 * Get credit balances and plan info
 * GET /agent-builder/credits
 */
export const getCredits = async app => {
  const url = `${getBaseUrl(app)}/credits`
  const response = await axios.get(url, { headers: getHeaders(app) })
  return response.data
}

/**
 * Get credit settings (auto-topup config)
 * GET /agent-builder/credits/settings
 */
export const getCreditsSettings = async app => {
  const url = `${getBaseUrl(app)}/credits/settings`
  const response = await axios.get(url, { headers: getHeaders(app) })
  return response.data
}

/**
 * Save credit settings (auto-topup config)
 * POST /agent-builder/credits/settings
 * Validation: autoTopupAmountUsd must be multiple of $25
 */
export const saveCreditsSettings = async (app, settings) => {
  const url = `${getBaseUrl(app)}/credits/settings`
  const response = await axios.post(url, settings, { headers: getHeaders(app) })
  return response.data
}

/**
 * Manual credit purchase
 * POST /agent-builder/credits/topup
 * Validation: amountUsd must be multiple of $25
 */
export const buyCredits = async (app, amountUsd) => {
  const url = `${getBaseUrl(app)}/credits/topup`
  const response = await axios.post(url, { amountUsd }, { headers: getHeaders(app) })
  return response.data
}

/**
 * Get usage summary for current billing cycle
 * GET /agent-builder/credits/usage
 */
export const getCreditsUsage = async app => {
  const url = `${getBaseUrl(app)}/credits/usage`
  const response = await axios.get(url, { headers: getHeaders(app) })
  return response.data
}

/**
 * Get paginated list of agent runs
 * GET /agent-builder/credits/usage/runs
 */
export const getCreditsUsageRuns = async (app, params = {}) => {
  const url = `${getBaseUrl(app)}/credits/usage/runs`
  const response = await axios.get(url, { headers: getHeaders(app), params })
  return response.data
}

/**
 * Get paginated topup history
 * GET /agent-builder/credits/topups
 */
export const getCreditsTopups = async (app, params = {}) => {
  const url = `${getBaseUrl(app)}/credits/topups`
  const response = await axios.get(url, { headers: getHeaders(app), params })
  return response.data
}

/**
 * Get static pricing config for the topup UI
 * GET /agent-builder/credits/pricing
 * Returns: { creditsPerDollar, dollarsPerCredit, topupOptions, minTopupUsd, topupIncrementUsd }
 */
export const getPricing = async app => {
  const url = `${getBaseUrl(app)}/credits/pricing`
  const response = await axios.get(url, { headers: getHeaders(app) })
  return response.data
}

/**
 * Get pending invoices that block further topups
 * GET /agent-builder/credits/pending-invoices
 * Returns: array of invoices with status payment_due or failed
 */
export const getPendingInvoices = async app => {
  const url = `${getBaseUrl(app)}/credits/pending-invoices`
  const response = await axios.get(url, { headers: getHeaders(app) })
  return response.data
}

/**
 * Retry payment for a pending invoice (sync invoice status with payment provider)
 * POST /agent-builder/credits/retry-payment
 * Body: { invoiceId }
 * Returns: { success, status, creditsAdded? }
 */
export const retryPayment = async (app, invoiceId) => {
  const url = `${getBaseUrl(app)}/credits/retry-payment`
  const response = await axios.post(url, { invoiceId }, { headers: getHeaders(app) })
  return response.data
}

/**
 * Get the AI agent plans catalog (names, descriptions, ids, prices, annual discount).
 * GET {customerDomain}agentic-plans
 * Account-level mgmt-api call (Bearer token), not an app-scoped agent-builder call.
 * Returns the raw `data` object keyed by plan slug (web-only/core/plus) + freePlans.
 */
export const getAgenticPlans = async () => {
  const ccBearerToken = localStorage.getItem('ccBearerToken')
  const url = `${constants.customerDomain}agentic-plans`
  const response = await axios.get(url, {
    headers: { Authorization: `Bearer ${ccBearerToken}` },
  })
  return response.data?.data ?? response.data
}
